/**
 * issue #76 Phase 5: 履歴 UI と FingeringPage で共有する詳細エラー文言。
 * 既存 FingeringPage.tsx:48-92 から切り出し、 DriveHistoryModal でも展開して表示できるようにする。
 *
 * 6 種類 (omr_timeout/mxl_missing/file_too_large/mode_mismatch/expired/watchdog) について
 * { title, cause?, actions[] } 構造で定義。 1 行ラベルは FINGERING_ERROR_LABEL を使う。
 */

export interface FingeringErrorDetail {
  title: string;
  cause?: string;
  actions: ReadonlyArray<string>;
}

export const FINGERING_ERROR_DETAILS = {
  omr_timeout: {
    title: '⚠️ 楽譜の解析が時間内に完了しませんでした',
    cause: '楽譜が大きすぎる、または総譜 (フルスコア) の可能性があります。',
    actions: [
      'パート譜 (1 パート分のみ) で再投入してみてください',
      'PDF を 1〜2 ページに分割して投入してみてください',
    ],
  },
  mxl_missing: {
    title: '⚠️ 楽譜から MXL データが取得できませんでした',
    cause:
      'Audiveris (楽譜認識エンジン) がこの PDF を完全にパースできなかったため、 音源生成や音源を含む再生成ができません。',
    actions: [
      '別の楽譜 PDF で試してください',
      'PDF が手書き / 低解像度 / 装飾的なフォントの場合、 認識が困難なことがあります',
      'スキャン PDF の場合、 より高解像度の版で試してください',
    ],
  },
  file_too_large: {
    title: '⚠️ ファイルサイズが大きすぎます',
    cause: '楽譜 PDF のサイズが上限 (30MB) を超えています。',
    actions: [
      'PDF を分割してアップロードしてください',
      'PDF を再保存して圧縮してください (Adobe Acrobat / Preview で「最適化」 オプション)',
      'より小さい解像度で再スキャンしてください',
    ],
  },
  mode_mismatch: {
    title: '⚠️ パート譜モードで投入されましたが、 総譜 (複数パート) が検出されました',
    cause: undefined,
    actions: [
      '「総譜モード」 を選んで Guitar1〜Guitar4 から目的のパートを指定して再投入してください',
      'またはパート譜 (1 パート分のみ) の PDF で再投入してください',
    ],
  },
  // issue #64: 逆方向③ (総譜モードなのに実体はパート譜) の早期検出 (error_kind は backend と一致必須)。
  mode_mismatch_reverse: {
    title: '⚠️ 総譜モードで投入されましたが、単一パート (パート譜) のようです',
    cause: '指定したパートが見つかりません。この PDF は 1 パート分 (パート譜) の可能性があります。',
    actions: [
      '「パート譜モード」を選んで再投入してください',
      '総譜 (複数パート) の場合は、実在するパート番号を指定してください',
    ],
  },
  // issue #62: 総譜とパート譜が 1 つの PDF に混在している早期検出 (error_kind は backend と一致必須)。
  mixed_score_part: {
    title: '⚠️ 総譜とパート譜が混在しているようです',
    cause: undefined,
    actions: [
      '総譜とパート譜を別々の PDF に分割してから再投入してください',
      '総譜のみ・パート譜のみであればそのまま投入できます',
    ],
  },
  expired: {
    title: '⚠️ ジョブの有効期限が切れました',
    cause: 'score-engine が結果を保持する 30 分を超えました。',
    actions: ['もう一度 PDF を投入してください'],
  },
  // issue #95 (#28): 自動保存 ON の expired。 失敗でなく「Drive 保存済みの可能性・要確認」。
  expired_autosaved: {
    title: '🔍 結果は Drive に保存された可能性があります',
    cause:
      '結果の保持期間 (30 分) を過ぎましたが、 自動保存が有効だったため Drive に保存済みの可能性があります。',
    actions: ['下の Drive 履歴を確認してください'],
  },
  watchdog: {
    title: '⚠️ 処理が制限時間 (30 分) を超えました',
    cause: undefined,
    actions: ['ファイルが大きい場合はページ数を減らして再投入してください'],
  },
} as const satisfies Record<string, FingeringErrorDetail>;

export type FingeringErrorKind = keyof typeof FINGERING_ERROR_DETAILS;

/** errorKind が既知種別なら詳細を返す。 未知 / undefined / 空文字は null。 */
export function getFingeringErrorDetail(
  errorKind: string | undefined,
): FingeringErrorDetail | null {
  if (!errorKind) return null;
  const detail = FINGERING_ERROR_DETAILS[errorKind as FingeringErrorKind];
  return detail ?? null;
}

/**
 * 1 行ラベル (履歴 UI の collapsed 表示用)。
 * 旧 DriveHistoryModal.ERROR_KIND_LABEL の後継、 error_processing / failed の fallback も追加。
 */
export const FINGERING_ERROR_LABEL: Record<string, string> = {
  omr_timeout: '楽譜認識タイムアウト',
  mxl_missing: '楽譜データ取得失敗',
  file_too_large: 'ファイルサイズ超過',
  mode_mismatch: 'モード不一致',
  mode_mismatch_reverse: 'モード不一致 (逆方向)',
  mixed_score_part: '総譜/パート譜混在',
  expired: '有効期限切れ',
  expired_autosaved: 'Drive 保存済みの可能性',
  watchdog: 'タイムアウト (30 分)',
  error_processing: '処理失敗',
  failed: '処理失敗',
};
